/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import * as DT from './CardiacDataTypes';
import { MakeFunctor } from '@/engine/DataFlow/Functor';
import { createBufferGeometry, emmanaged, emmanaged2 } from '@/engine/PlanningAlgorithm/MatSDKHelpers';
import { makeTransformation } from '@/engine/PlanningAlgorithm/Helpers';
import type * as MatSDKTypes from '@/engine/WasmModules/MatSDK';
import MatSDK from '@/engine/WasmModules/MatSDK';
import * as THREE from 'three';
import { rgbColorMapper } from '@/engine/utils/colorUtils';
import { areaMeasurementParams, emptyPlane } from '@/engine/PlanningAlgorithm/Functors';
import { CatmullRomCurve3Type, getTriangleNormalPrecise } from '@/engine/utils/geometryHelper';
import { ensureDefined } from '@/engine/utils/matSdkHelpers';

export * from '@/engine/PlanningAlgorithm/Functors';

export function _rayCast(
  tree: MatSDKTypes.KDTree3d,
  surf: MatSDKTypes.TriangleSurface,
  origin: THREE.Vector3,
  direction: THREE.Vector3
): THREE.Vector3 | undefined {
  return emmanaged2(
    new MatSDK.Double3().setFromArray(origin.toArray()),
    new MatSDK.Double3().setFromArray(direction.toArray()),
    (pt1: MatSDKTypes.Double3, pt2: MatSDKTypes.Double3) =>
      emmanaged(new MatSDK.Ray().set(pt1, pt2), (ray) => {
        const result = tree.intersectionTriangleSurface(surf, ray);
        if (result.triangleIndex >= surf.getRawTriangles().length / 3) {
          return undefined;
        }
        return new THREE.Vector3().fromArray(result.point.toArray());
      })
  );
}

export enum _rayCast2BreakKind {
  BreakOut = 'BreakOut',
  BreakIn = 'BreakIn'
}

export interface _rayCast2ResultItem {
  point: THREE.Vector3;
  breakKind: _rayCast2BreakKind;
  distance: number;
}

export function _rayCast2(
  tree: MatSDKTypes.KDTree3d,
  surf: MatSDKTypes.TriangleSurface,
  origin: THREE.Vector3,
  direction: THREE.Vector3
): Array<_rayCast2ResultItem> {
  return emmanaged2(
    new MatSDK.Double3().setFromArray(origin.toArray()),
    new MatSDK.Double3().setFromArray(direction.toArray()),
    (pt1: MatSDKTypes.Double3, pt2: MatSDKTypes.Double3) =>
      emmanaged(new MatSDK.Ray().set(pt1, pt2), (ray) => {
        const indices = surf.getRawTriangles();
        const vertices = surf.getRawVertices();
        const results = tree.intersectionsTriangleSurface(surf, ray);
        const resInfos = new Array<_rayCast2ResultItem>();
        for (let i = 0, sz = results.size(); i < sz; ++i) {
          const result = ensureDefined(results.get(i));
          const idx = result.triangleIndex * 3;
          const triN = getTriangleNormalPrecise(vertices, indices, idx);
          const info = {
            point: new THREE.Vector3().fromArray(result.point.toArray()),
            breakKind: triN.dot(direction) > 0 ? _rayCast2BreakKind.BreakOut : _rayCast2BreakKind.BreakIn,
            distance: result.distance
          };
          resInfos.push(info);
        }
        resInfos.sort((a, b) => a.distance - b.distance);
        return resInfos;
      })
  );
}

export function _sliceInside(
  surf: MatSDKTypes.TriangleSurface,
  normal: THREE.Vector3,
  origin: THREE.Vector3
): { points: Array<THREE.Vector3>; isClosed: boolean } {
  return emmanaged(
    emmanaged2(
      new MatSDK.Double3().set(normal.x, normal.y, normal.z),
      new MatSDK.Double3().set(origin.x, origin.y, origin.z),
      (n, o) => MatSDK.sliceInside(surf, n, o, false, 0.0, true)
    ),
    (polyline) => {
      const rawPts = polyline.getRawPoints();
      const points: Array<THREE.Vector3> = [];
      for (let j = 0; j < rawPts.length; j += 3) {
        points.push(new THREE.Vector3().fromArray(rawPts, j));
      }
      return { points: points, isClosed: polyline.closed };
    }
  );
}

export function _sliceInsideResampled(
  surf: MatSDKTypes.TriangleSurface,
  normal: THREE.Vector3,
  origin: THREE.Vector3
): { points: Array<THREE.Vector3>; isClosed: boolean } {
  return emmanaged(
    emmanaged2(
      new MatSDK.Double3().set(normal.x, normal.y, normal.z),
      new MatSDK.Double3().set(origin.x, origin.y, origin.z),
      (n, o) => MatSDK.sliceInside(surf, n, o, false, 0.0, true)
    ),
    (polyline) =>
      emmanaged(polyline.resampled(12), (resampledPolyline) => {
        const rawPts = resampledPolyline.getRawPoints();
        const points: Array<THREE.Vector3> = [];
        for (let j = 0; j < rawPts.length; j += 3) {
          points.push(new THREE.Vector3().fromArray(rawPts, j));
        }
        return { points: points, isClosed: resampledPolyline.closed };
      })
  );
}

export function _catmullRomSpline(
  c1: THREE.Vector3,
  n1: THREE.Vector3,
  c2: THREE.Vector3,
  n2: THREE.Vector3,
  accuracyMm: number
): Array<THREE.Vector3> {
  const points: Array<THREE.Vector3> = [];

  points.push(c1.clone());

  const beg = c1.clone().addScaledVector(n1, accuracyMm);
  const mid1 = c1.clone().addScaledVector(n1, accuracyMm * 2);
  const mid2 = c2.clone().addScaledVector(n2, -accuracyMm * 2);
  const end = c2.clone().addScaledVector(n2, -accuracyMm);
  const spline = new THREE.CatmullRomCurve3([beg, mid1, mid2, end], false, CatmullRomCurve3Type.Chordal);

  const dist = c1.distanceTo(c2);
  const num = Math.round((Math.PI * Math.sqrt((dist * dist) / 2)) / 2 / accuracyMm);
  const splinePts = spline.getSpacedPoints(num);

  points.push(...splinePts);

  points.push(c2);

  return points;
}

export const sliceAortaWithPlane = MakeFunctor(
  'FT.sliceAortaWithPlane',
  (aortaSurface: DT.MatTriangleSurface, slicingPlane: DT.Plane): DT.Polyline => {
    const pl = slicingPlane.value.planeEq;
    const pt = slicingPlane.value.origin;
    return new DT.Polyline(_sliceInsideResampled(aortaSurface.value, pl.normal, pt));
  }
);

export const getCardiacManufacturer = MakeFunctor(
  'FT.getCardiacManufacturer',
  (instr: DT.InstrumentationLibrary, manufacturer: DT.StringT): DT.CardiacManufacturer => {
    return instr.getManufacturerAs(DT.CardiacManufacturer, manufacturer.value);
  }
);

export const centerlineDistancesVertexColorMask = MakeFunctor(
  'FT.centerlineDistancesVertexColorMask',
  (
    surf: DT.MatTriangleSurface,
    centerline: DT.MatLineSegments,
    eTree: DT.MatKDTree3d,
    distances: DT.MatDoublesVector,
    allowanceRange: DT.RangeT
  ): DT.Mesh => {
    const mapper = rgbColorMapper([allowanceRange.value[0], allowanceRange.value[1]], [0, 120], 1.0, 0.4);
    const col = emmanaged(
      MatSDK.centerlineDistancesVertexMask(surf.value, centerline.value, eTree.value, distances.value, 5),
      (vector) => {
        const colors = new Float32Array(vector.size() * 3);
        for (let i = 0, sz = vector.size(); i < sz; ++i) {
          const val = ensureDefined(vector.get(i));
          const j = i * 3;
          if (val < 0) {
            colors[j] = 1;
            colors[j + 1] = 0.5;
            colors[j + 2] = 0.5;
          } else {
            const color = mapper(val);
            colors[j] = color[0];
            colors[j + 1] = color[1];
            colors[j + 2] = color[2];
          }
        }
        return colors;
      }
    );
    return new DT.Mesh(createBufferGeometry(surf.value, col));
  }
);

export const calculateToSplineDistances = MakeFunctor(
  'FT.calculateToSplineDistances',
  (polyline: DT.MatLineSegments, spline: DT.MatLineSegments, edgeTree: DT.MatKDTree3d): DT.MatDoublesVector => {
    const ret = new MatSDK.VectorDouble();
    const pairs = polyline.value.getRawSegments();
    if (pairs.length > 0) {
      ret.resize(pairs.length / 2, -1);
      const coords = polyline.value.getRawVertices();
      for (let i = 0, sz = pairs.length / 2; i < sz; ++i) {
        const j = i * 2;
        const k = pairs[j] * 3;
        const res = emmanaged(new MatSDK.Double3().set(coords[k], coords[k + 1], coords[k + 2]), (pt) =>
          edgeTree.value.findNearestEdgePoint(spline.value, pt, 10000.0)
        );
        ret.set(i, res.distance);
      }
    }
    return new DT.MatDoublesVector(ret);
  }
);

export const polylineToSplineSmallestDistance = MakeFunctor(
  'FT.polylineToSplineSmallestDistance',
  (
    polyline: DT.MatLineSegments,
    spline: DT.MatLineSegments,
    edgeTree: DT.MatKDTree3d,
    zVector: DT.Vector,
    yVector: DT.Vector
  ): DT.DistanceMeasurement => {
    let min: THREE.Line3 | undefined = undefined;
    const pairs = polyline.value.getRawSegments();
    if (pairs.length > 0) {
      const coords = polyline.value.getRawVertices();
      for (let i = 0, sz = pairs.length / 2; i < sz; ++i) {
        const j = i * 2;
        const k = pairs[j] * 3;
        const res = emmanaged(new MatSDK.Double3().set(coords[k], coords[k + 1], coords[k + 2]), (pt) =>
          edgeTree.value.findNearestEdgePoint(spline.value, pt, 10000.0)
        );
        if (min === undefined || res.distance < min.distance()) {
          min = new THREE.Line3(
            new THREE.Vector3().fromArray(coords, k),
            new THREE.Vector3().fromArray(res.point.toArray())
          );
        }
      }
    }
    if (!min) {
      throw new Error('Undefined');
    }
    let orientationY = yVector.value.clone();
    let origin = min.end;
    if (min.distanceSq() > 0) {
      orientationY = min.delta(new THREE.Vector3()).normalize();
      if (orientationY.dot(yVector.value) < 0) {
        orientationY.negate();
        origin = min.start;
      }
    }
    const orientationZ = zVector.value.clone().cross(orientationY).negate().cross(orientationY).normalize();
    const orientationX = orientationY.clone().cross(orientationZ).normalize();
    const transformFrom = makeTransformation(origin, orientationX, orientationY, orientationZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(min.start.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(min.end.clone().applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(15, 15, 20));
    bBox.max.add(new THREE.Vector3(15, 15, 20));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);
    const location = {
      ptO: origin,
      dirY: orientationY,
      dirZ: orientationZ,
      minCorner: minCorner,
      maxCorner: maxCorner
    };
    return new DT.DistanceMeasurement(min.distance(), min, location);
  }
);

export const calculateCenterline = MakeFunctor(
  'FT.calculateCenterline',
  (surface: DT.MatTriangleSurface, precision: DT.NumberT): DT.MatPolylinesVector => {
    return new DT.MatPolylinesVector(MatSDK.centerline3d(surface.value, precision.value, 0)); // keep thinFeaturesSize=0
  }
);

export const shortestPathOverPolylines = MakeFunctor(
  'FT.shortestPathOverPolylines',
  (centerline: DT.MatPolylinesVector, pt1: DT.Point, pt2: DT.Point): DT.MatPolyline => {
    const poly = emmanaged2(
      new MatSDK.Double3().setFromArray(pt1.value.toArray()),
      new MatSDK.Double3().setFromArray(pt2.value.toArray()),
      (point1, point2) => MatSDK.shortestPathOverPolylines(centerline.value, point1, point2)
    );
    return new DT.MatPolyline(poly);
  }
);

export const polylineStartPoint = MakeFunctor('FT.polylineStartPoint', (poly: DT.MatPolyline): DT.Point => {
  const pt = poly.value.accessPoints().access(0);
  return new DT.Point(new THREE.Vector3(pt.x, pt.y, pt.z));
});

export const stitchMatPolylines = MakeFunctor(
  'FT.stitchMatPolylines',
  (poly1: DT.MatPolyline, poly2: DT.MatPolyline): DT.MatPolyline => {
    const pts1 = poly1.value.accessPoints();
    const pts2 = poly2.value.accessPoints();
    const rawPts = new Float32Array((pts1.size() + pts2.size()) * 3);
    let j = 0;
    for (let i = 0, sz = pts1.size(); i < sz; ++i, j += 3) {
      const pt = pts1.access(i);
      rawPts[j] = pt.x;
      rawPts[j + 1] = pt.y;
      rawPts[j + 2] = pt.z;
    }
    for (let i = 0, sz = pts2.size(); i < sz; ++i, j += 3) {
      const pt = pts2.access(i);
      rawPts[j] = pt.x;
      rawPts[j + 1] = pt.y;
      rawPts[j + 2] = pt.z;
    }
    const poly = new MatSDK.Polyline3d();
    poly.setFromRaw(rawPts, false);
    return new DT.MatPolyline(poly);
  }
);

export const smoothenPolyline = MakeFunctor(
  'FT.smoothenPolyline',
  (polyline: DT.MatPolyline, numIterations: DT.NumberT, smoothingFactor: DT.NumberT): DT.MatPolyline => {
    return new DT.MatPolyline(polyline.value.smoothed(numIterations.value, smoothingFactor.value));
  }
);

export const planeShiftRange = MakeFunctor(
  'FT.planeShiftRange',
  (plane: DT.Plane, posNegDisplacement: DT.NumberT): DT.Line => {
    return new DT.Line(
      new THREE.Line3(
        plane.value.origin.clone().addScaledVector(plane.value.planeEq.normal, -posNegDisplacement.value),
        plane.value.origin.clone().addScaledVector(plane.value.planeEq.normal, posNegDisplacement.value)
      )
    );
  }
);

export const planeShiftRangeLimited = MakeFunctor(
  'FT.planeShiftRangeLimited',
  (plane: DT.Plane, point: DT.Point, posNegDisplacement: DT.NumberT): DT.Line => {
    const dist = plane.value.planeEq.distanceToPoint(point.value);
    return new DT.Line(
      new THREE.Line3(
        plane.value.origin
          .clone()
          .addScaledVector(
            plane.value.planeEq.normal,
            dist < 0.01 ? Math.max(dist - 0.01, -posNegDisplacement.value) : -posNegDisplacement.value
          ),
        plane.value.origin
          .clone()
          .addScaledVector(
            plane.value.planeEq.normal,
            dist > -0.01 ? Math.min(dist + 0.01, posNegDisplacement.value) : posNegDisplacement.value
          )
      )
    );
  }
);
export const calculateDiameters = MakeFunctor(
  'FT.calculateDiameters',
  (surf: DT.MatTriangleSurface, centerline: DT.MatLineSegments): DT.MatDoublesVector => {
    const seg = centerline.value.getRawSegments();
    if (seg.length == 0) {
      return new DT.MatDoublesVector(new MatSDK.VectorDouble());
    }
    const pts = centerline.value.getRawVertices();
    const ret = new MatSDK.VectorDouble();
    ret.resize(seg.length / 2, -1);
    for (let i = 0, sz = seg.length / 2; i < sz; ++i) {
      const idx1 = seg[i * 2];
      const idx2 = seg[i * 2 + 1];
      const origin = new THREE.Vector3().fromArray(pts, idx1 * 3);
      const normal = new THREE.Vector3().fromArray(pts, idx2 * 3).sub(origin);
      try {
        const slice = _sliceInsideResampled(surf.value, normal, origin);
        ret.set(idx1, areaMeasurementParams(slice.points, slice.isClosed, 90)?.dMean ?? -1);
      } catch (e: unknown) {
        console.warn(e);
        ret.set(idx1, -1);
      }
    }
    if (ret.size() > 1) {
      for (let i = 1, sz = ret.size(); i < sz; ++i) {
        if (ensureDefined(ret.get(i)) < 0) ret.set(i, ensureDefined(ret.get(i - 1)));
      }
      for (let i = ret.size() - 2; i >= 0; --i) {
        if (ensureDefined(ret.get(i)) < 0) ret.set(i, ensureDefined(ret.get(i + 1)));
      }
    }
    return new DT.MatDoublesVector(ret);
  }
);

export const minSlicePlaneAlongPolyline = MakeFunctor(
  'FT.minSlicePlaneAlongPolyline',
  (
    centerline: DT.MatLineSegments,
    eTree: DT.MatKDTree3d,
    diameters: DT.MatDoublesVector,
    poly: DT.MatPolyline
  ): DT.Plane => {
    const seg = centerline.value.getRawSegments();
    if (seg.length == 0) {
      return new DT.Plane({ origin: new THREE.Vector3(), planeEq: new THREE.Plane() });
    }
    const pts = poly.value.accessPoints();
    if (seg.length == 0 || pts.size() == 0 || diameters.value.size() == 0) {
      return emptyPlane();
    }
    let idx = -1,
      min = 0;
    for (let i = 0, sz = pts.size() - 1; i < sz; ++i) {
      const pt1 = new THREE.Vector3().fromArray(pts.access(i).toArray());
      const pt2 = new THREE.Vector3().fromArray(pts.access(i + 1).toArray());
      const point = pt1.add(pt2).divideScalar(2);
      const edgeIndex = emmanaged(
        new MatSDK.Double3().set(point.x, point.y, point.z),
        (pt) => eTree.value.findNearestEdgePoint(centerline.value, pt, 10000.0).edgeIndex
      );
      const j = seg[edgeIndex * 2];
      const diam = ensureDefined(diameters.value.get(j));
      if (diam > 0) {
        if (idx < 0 || diam < min) {
          idx = edgeIndex;
          min = diam;
        }
      } else {
        console.warn('Uninitialized diameter at', i);
      }
    }
    const idx1 = seg[idx * 2];
    const idx2 = seg[idx * 2 + 1];
    const vert = centerline.value.getRawVertices();
    const pt1 = new THREE.Vector3().fromArray(vert, idx1 * 3);
    const pt2 = new THREE.Vector3().fromArray(vert, idx2 * 3);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(pt2.sub(pt1).normalize(), pt1);
    return new DT.Plane({ planeEq: plane, origin: pt1 });
  }
);

export const centerlineDiametersVertexColorMask = MakeFunctor(
  'FT.centerlineDiametersVertexColorMask',
  (
    surf: DT.MatTriangleSurface,
    centerline: DT.MatLineSegments,
    eTree: DT.MatKDTree3d,
    diameters: DT.MatDoublesVector,
    catheterSize: DT.NumberT,
    allowanceRange: DT.RangeT
  ): DT.ArrayT => {
    const radius = catheterSize.value / 3 / 2;
    const mapper = rgbColorMapper(
      [radius * allowanceRange.value[0], radius * allowanceRange.value[1]],
      [0, 120],
      1.0,
      0.4
    );
    const col = emmanaged(
      MatSDK.centerlineDiametersVertexMask(surf.value, centerline.value, eTree.value, diameters.value),
      (vector) => {
        const colors = new Array<number>(vector.size() * 4);
        for (let i = 0, sz = vector.size(); i < sz; ++i) {
          const val = ensureDefined(vector.get(i));
          const j = i * 4;
          if (val < 0) {
            colors[j] = 1;
            colors[j + 1] = 0.5;
            colors[j + 2] = 0.5;
            colors[j + 3] = 1;
          } else {
            const color = mapper(val / 2);
            colors[j] = color[0];
            colors[j + 1] = color[1];
            colors[j + 2] = color[2];
            colors[j + 3] = 1;
          }
        }
        return colors;
      }
    );
    return new DT.ArrayT(col);
  }
);

export const slicePlaneAtPoint = MakeFunctor(
  'FT.slicePlaneAtPoint',
  (centerline: DT.MatLineSegments, eTree: DT.MatKDTree3d, point: DT.Point): DT.Plane => {
    const seg = centerline.value.getRawSegments();
    if (seg.length == 0) {
      return new DT.Plane({ origin: new THREE.Vector3(), planeEq: new THREE.Plane() });
    }
    const res = emmanaged(new MatSDK.Double3().set(point.value.x, point.value.y, point.value.z), (pt) =>
      eTree.value.findNearestEdgePoint(centerline.value, pt, 10000.0)
    );
    if (res.edgeIndex > seg.length / 2) {
      console.warn('Segment not found');
      return emptyPlane();
    }
    const idx1 = seg[res.edgeIndex * 2];
    const idx2 = seg[res.edgeIndex * 2 + 1];
    const pts = centerline.value.getRawVertices();
    const pt1 = new THREE.Vector3().fromArray(pts, idx1 * 3);
    const pt2 = new THREE.Vector3().fromArray(pts, idx2 * 3);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(pt2.sub(pt1).normalize(), pt1);
    return new DT.Plane({ planeEq: plane, origin: pt1 });
  }
);

export const makeCenterline = MakeFunctor(
  'FT.makeCenterline',
  (polyline: DT.Polyline, ...planes: Array<DT.Plane>): DT.Centerline => {
    if (polyline.value.isClosed) {
      throw new Error('Closed centerline is unexpected');
    }
    const points = polyline.value.points;
    const normals = new Array<THREE.Vector3>(points.length);
    for (let i = 0; i < points.length - 1; ++i) {
      normals[i] = points[i + 1].clone().sub(points[i]).normalize();
    }
    normals[normals.length - 1] = normals[normals.length - 2];

    const indices: Array<{ idx: number; minDist: number; pl: THREE.Plane }> = new Array(planes.length);
    for (let i = 0; i < planes.length; ++i) {
      indices[i] = { idx: -1, minDist: -1, pl: planes[i].value.planeEq };
    }

    for (let i = 0; i < points.length; ++i) {
      for (const idx of indices) {
        const dist = Math.abs(idx.pl.distanceToPoint(points[i]));
        if (idx.idx < 0 || dist < idx.minDist) {
          idx.idx = i;
          idx.minDist = dist;
        }
      }
    }

    const cnt = 10;
    indices.sort((a, b) => a.idx - b.idx);
    for (let j = 0; j < indices.length; ++j) {
      const index = indices[j];
      normals[index.idx] = index.pl.normal.clone();
      let prevCnt = cnt;
      if (j > 0) {
        const prevIndex = indices[j - 1];
        const mid = Math.floor((index.idx - prevIndex.idx) / 2);
        if (mid <= cnt) {
          prevCnt = mid;
          //console.warn('Measurements are too close to each other for smooth transfer');
        }
      } else {
        prevCnt = Math.min(cnt, index.idx);
      }
      const negVec = normals[index.idx - prevCnt].clone().sub(normals[index.idx]).divideScalar(prevCnt);
      for (let i = 1; i <= prevCnt; ++i) {
        normals[index.idx - i] = normals[index.idx - i + 1].clone().add(negVec);
      }
      let nextCnt = cnt;
      if (j < indices.length - 1) {
        const nextIndex = indices[j + 1];
        const mid = Math.floor((nextIndex.idx - index.idx) / 2);
        if (mid <= cnt) {
          nextCnt = mid;
          //console.warn('Measurements are too close to each other for smooth transfer');
        }
      } else {
        nextCnt = Math.min(cnt, normals.length - index.idx - 1);
      }
      const posVec = normals[index.idx + nextCnt].clone().sub(normals[index.idx]).divideScalar(nextCnt);
      for (let i = 1; i <= nextCnt; ++i) {
        normals[index.idx + i] = normals[index.idx + i - 1].clone().add(posVec);
      }
    }

    return new DT.Centerline({ points: points, normals: normals });
  }
);

export const makeCenterlineSmoothNormals = MakeFunctor(
  'FT.makeCenterlineSmoothNormals',
  (polyline: DT.Polyline, ...planes: Array<DT.Plane>): DT.Polyline => {
    if (polyline.value.isClosed) {
      throw new Error('Closed centerline is unexpected');
    }
    const points = polyline.value.points;
    const normals = new Array<THREE.Vector3>(points.length);
    if (planes.length == 0) {
      for (let i = 0; i < points.length - 1; ++i) {
        normals[i] = points[i + 1].clone().sub(points[i]).normalize();
      }
      normals[normals.length - 1] = normals[normals.length - 2];
    } else {
      const indices: Array<{ idx: number; minDist: number; pl: THREE.Plane }> = [];
      for (const plane of planes) {
        indices.push({ idx: -1, minDist: -1, pl: plane.value.planeEq });
      }

      for (let i = 0; i < points.length; ++i) {
        for (const idx of indices) {
          const dist = Math.abs(idx.pl.distanceToPoint(points[i]));
          if (idx.idx < 0 || dist < idx.minDist) {
            idx.idx = i;
            idx.minDist = dist;
          }
        }
      }

      indices.sort((a, b) => a.idx - b.idx);
      const first = indices[0];
      for (let i = 0; i <= first.idx; ++i) {
        normals[i] = first.pl.normal;
      }
      const last = indices[indices.length - 1];
      for (let i = last.idx + 1; i < normals.length; ++i) {
        normals[i] = last.pl.normal;
      }
      for (let i = 1; i < indices.length; ++i) {
        const prev = indices[i - 1];
        const curr = indices[i];
        const vec = curr.pl.normal
          .clone()
          .sub(prev.pl.normal)
          .divideScalar(curr.idx - prev.idx);
        for (let j = prev.idx + 1; j <= curr.idx; ++j) {
          normals[j] = normals[j - 1].clone().add(vec);
        }
      }
    }
    return new DT.Polyline({ points: normals, isClosed: false });
  }
);

export const adjustPlaneCL = MakeFunctor('FT.adjustPlaneCL', (pl: DT.Plane, poly: DT.Polyline): DT.Plane => {
  const pts = poly.value.points;
  let j = -1;
  let min = -1;
  for (let i = 0; i < pts.length - 1; ++i) {
    const dist = Math.abs(pl.value.planeEq.distanceToPoint(pts[i]));
    if (j < 0 || dist < min) {
      min = dist;
      j = i;
    }
  }
  return new DT.Plane({
    origin: pts[j],
    planeEq: pl.value.planeEq.clone()
  });
});

export const getSystem = MakeFunctor(
  'FT.getCardiacSystem',
  (manufacturer: DT.IManufacturer, system: DT.StringT): DT.CardiacSystem => {
    return manufacturer.getSystemAs(DT.CardiacSystem, system.value);
  }
);

export const getValveInsertionDepth = MakeFunctor(
  'FT.getDefaultValveInsertionDepth',
  (system: DT.CardiacSystem): DT.NumberT => {
    return new DT.NumberT(system.metadata.valveInsertionDepth);
  }
);

export const getValveInsertionDepthRange = MakeFunctor(
  'FT.getDefaultValveInsertionDepthRange',
  (system: DT.CardiacSystem): DT.RangeT => {
    return new DT.RangeT(system.metadata.valveInsertionDepthRange);
  }
);
